import { useDispatch, useSelector } from "react-redux";
import ToggleButton from "../components/toggleButton/toggleButton";
import {
  getUserData,
  getUserDataTimeZone,
  setLogout,
  setUserDataTimeZone,
  setUserDataToothNotation,
  setUserDataWarnings,
} from "../store/login.slice";
import {
  useSetPasswordMutation,
  useSetTimeZoneMutation,
  useUpdateToothNotationMutation,
  useUpdateWarningsMutation,
} from "../api/accountSlice/accountSlice";
import TimezoneSelect, { ITimezone } from "react-timezone-select";
import { useState } from "react";
import Modal from "../components/modal/Modal";
import { toast } from "react-toastify";
import { Eye } from "lucide-react";
import { useSubmitFeedbackMutation } from "../api/feedbackSlice/feedbackSlice";
import useTokenSpoilCheck from "../hooks/tokenspoilcheck";
import { daysUntil, formatDateTime } from "../helpers/formatters";
import { getAuthData, invalidateToken } from "../helpers/auth";
import useSetUserTheme from "../hooks/useSetUserTheme";
import styles from "./accountSettings.module.scss";

// Same three values the backend accepts (ToothNotation enum)
const TOOTH_NOTATIONS = [
  { value: "FDI", label: "FDI (e.g. 36)" },
  { value: "Universal", label: "Universal (e.g. #19)" },
  { value: "Both", label: "Both (e.g. 36 / #19)" },
];

function AccountSettings() {
  //
  //Selectors
  //
  const userData = useSelector(getUserData);
  const timeZone = useSelector(getUserDataTimeZone);
  const DLOSCEMode = userData.exam_type;
  //
  //local state
  //
  //Feedback modal: status is "idle" | "loading" | "success" | "error"
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    status: "idle",
  });
  const [errorStatusCode, setErrorStatusCode] = useState(undefined);
  const [showPassword, setShowPassword] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwords, setPasswords] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [last4Dgitis, setLast4Digits] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [passwordError, setPasswordError] = useState<undefined | string>();
  const [difficultyLevel, setDifficultyLevel] = useState(userData.difficulty);
  const [toothNotation, setToothNotation] = useState(
    userData.tooth_notation || "FDI"
  );
  const [selectedTimezone, setSelectedTimezone] = useState<ITimezone>(
    userData.time_zone
      ? userData.time_zone
      : Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  //
  //Hooks
  //
  const dispatch = useDispatch();

  //
  //Feedback (same payload as the other apps, sent as OSCEDENTAL)
  //
  const [sendFeedback] = useSubmitFeedbackMutation();

  const openFeedback = () => {
    setFeedback({ open: true, message: "", status: "idle" });
  };

  const closeFeedback = () => {
    if (feedback.status === "loading") return;
    setFeedback({ open: false, message: "", status: "idle" });
  };

  const submitFeedback = async () => {
    const text = feedback.message.trim();
    if (!text || feedback.status === "loading") return;
    setFeedback((prev) => ({ ...prev, status: "loading" }));

    const contextText = [].filter(Boolean).join("\n");
    const payload = {
      customer_name:
        userData?.customer_first_name + " " + userData?.customer_last_name,
      product: "OSCEDENTAL",
      customer_id: userData?.customer_id,
      customer_email: userData?.customer_email,
      case_number: 0,
      question_id: null,
      message: contextText ? `${text}\n\n---\n${contextText}` : text,
      reviewed: false,
      card_number_id: null,
      key: "OSCEDENTAL",
    };

    try {
      await sendFeedback(payload).unwrap();
      setFeedback((prev) => ({ ...prev, status: "success" }));
      setTimeout(() => {
        setFeedback({ open: false, message: "", status: "idle" });
      }, 1800);
    } catch (error) {
      console.error("Error sending feedback:", error);
      setErrorStatusCode(error?.status);
      setFeedback((prev) => ({ ...prev, status: "error" }));
    }
  };

  useTokenSpoilCheck(errorStatusCode);

  //
  //Queries
  const { applyTheme } = useSetUserTheme();
  //
  const [setTimeZone] = useSetTimeZoneMutation();

  const [setPassword] = useSetPasswordMutation();

  const [updateWarnings] = useUpdateWarningsMutation();

  const [updateToothNotation, { isLoading: toothNotationSaving }] =
    useUpdateToothNotationMutation();

  //
  //Util funcs
  //
  async function setUserTheme(theme) {
    let result = undefined;
    try {
      result = await applyTheme(theme);
      toast("Theme set");
    } catch (error) {
      console.log(error);
      setErrorStatusCode(error?.status);
      return;
    }
    toast(
      result?.error?.data?.message
        ? result?.error?.data?.message
        : result?.data?.message
    );
  }
  async function setUserTimeZone(timeZone) {
    try {
      await setTimeZone({
        time_zone: JSON.stringify(timeZone),
      }).unwrap();
    } catch (error) {
      setErrorStatusCode(error?.status);
      console.log(error);
      return;
    }
    toast("Timezone updated!");
    setSelectedTimezone(timeZone);
    dispatch(setUserDataTimeZone({ time_zone: timeZone }));
  }
  async function setUserToothNotation(notation) {
    const previous = toothNotation;
    setToothNotation(notation);
    try {
      await updateToothNotation({ tooth_notation: notation }).unwrap();
    } catch (error) {
      setToothNotation(previous);
      setErrorStatusCode(error?.status);
      toast(error?.data?.message ?? "Couldn't update tooth notation");
      return;
    }
    dispatch(setUserDataToothNotation({ tooth_notation: notation }));
    toast("Tooth notation updated!");
  }
  async function setUserPassword() {
    if (passwords.new_password != passwords.confirm_password) {
      setPasswordError("New passwords do not match");
      return;
    }
    let result = undefined;
    try {
      result = await setPassword({
        password: {
          old_password: passwords.old_password,
          new_password: passwords.new_password,
        },
      }).unwrap();
      if (result?.error?.data?.message) {
        console.log("Failed to set password");
        setPasswordError(result?.error?.data?.message);
        setPasswords({
          old_password: "",
          new_password: "",
          confirm_password: "",
        });
        return;
      }
    } catch (error) {
      console.log("Failed to set password", error);
      setErrorStatusCode(error?.status);
      setPasswordError(error.data.message);
      setPasswords({
        old_password: "",
        new_password: "",
        confirm_password: "",
      });
      return;
    }
    toast(result.message);
    setPasswordError(undefined);
    setChangePasswordOpen(false);
  }
  function logout() {
    invalidateToken();
    dispatch(setLogout());
  }
  async function fetchReceipt() {
    const loginData = JSON.parse(getAuthData());
    const tokenCookie = loginData?.token;
    fetch(`${import.meta.env.VITE_BACKEND_URL}/sendreceipt.webapi`, {
      method: "POST",
      headers: {
        TOKEN: tokenCookie,
      },
      body: JSON.stringify({
        last4: Number(last4Dgitis),
      }),
    })
      .then((response) => response.text())
      .then((html) => {
        html = html.replace(
          `<div style="text-align: center;">`,
          `<div style="text-align: center;display: flex;justify-content: center;">`
        );
        setHtmlContent(html);
      })
      .catch((error) => console.error("Error fetching HTML:", error));
  }
  const handlePrint = () => {
    const windowPrint = window.open("", "_blank");
    windowPrint.document.write(`${htmlContent}`);
    windowPrint.document.close();
    windowPrint.focus();
    windowPrint.print();
    windowPrint.close();
  };

  const slugButton = async () => {
    try {
      // Get the URL from the environment variable
      const url = import.meta.env.VITE_SLUG_URL + "/getslug.webapi";
      console.log("About to send request to ", url);
      if (!url) {
        console.error("Environment variable VITE_SLUG_URL is not defined");
        alert("Server URL is not configured properly");
        return;
      }

      // Prepare the request body
      const requestBody = {
        email: userData.customer_email,
        password: userData.password,
        productName: "Oscedental",
      };

      // Make the POST request
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      // Handle the response
      if (response.ok) {
        const data = await response.json();
        console.log("Success:", data);
        if (data && data.container && data.container.Slug) {
          const extractedSlug = data.container.Slug;
          if (DLOSCEMode == "DLOSCE") {
            window.location.href = `https://dl.oscedental.com/purchase?slug=${extractedSlug}`;
          } else {
            window.location.href = `https://www.oscedental.com/purchase?slug=${extractedSlug}`;
          }
          console.log("Extracted Slug:", extractedSlug);
        } else {
          console.error("Slug not found in the response");
        }
      } else {
        console.error("Error:", response.statusText);
        alert(`Request failed: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error:", error);
      alert(`An error occurred: ${error.message}`);
    }
  };

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerInner}>
          <span className={styles.stepNumber}>Account Settings</span>
          <button
            className={styles.logoutButton}
            onClick={logout}
          >
            Logout
          </button>
        </div>
      </div>
      <div className={styles.cardPrimary}>
        {/*"Profile details"*/}
        <span className={styles.bold}>Profile Details</span>
        <div className={styles.details}>
          <div className={styles.detailRow}>
            <span>Name</span>
            <span>{userData.customer_email}</span>
          </div>
          <div className={styles.detailRow}>
            <span>Username</span>
            <span>
              {userData.customer_first_name} {userData.customer_last_name}
            </span>
          </div>
          <div className={styles.detailRow}>
            <span>Account Creation Date</span>
            <span>
              {formatDateTime(
                timeZone.value,
                new Date(userData.date_cust_created)
              )}
            </span>
          </div>
          <div className={styles.detailRow}>
            <span>Demo</span>
            <span>{userData.demo ? "Yes" : "No"}</span>
          </div>
          <div className={styles.detailRow}>
            <span>Subscription Expiration</span>
            <span>
              {formatDateTime(
                timeZone.value,
                new Date(userData.license_expiration)
              )}
            </span>
          </div>
        </div>
      </div>
      {/*"Theme"*/}
      <div className={styles.cardPrimary}>
        {/**/}
        <div className={styles.themeSection}>
          <span className={styles.sectionTitle}>Theme</span>
          <span className={styles.spacedText}>
            Personalize your studying by selecting a theme that feels more like
            you.
          </span>
          <hr />
          <div className={styles.grid}>
            <div className={styles.themeOption}>
              <div className={styles.column}>
                <span className={styles.cardTitle}>Classic</span>
                <span className={styles.small}>
                  Our personal favorite - A modest design with high contrast
                  accents
                </span>
                <div className={`${styles.swatches} theme-default`}>
                  <div className={styles.swatchPrimary}></div>
                  <div className={styles.swatchSecondary}></div>
                  <div className={styles.swatchPrimaryLight}></div>
                  <div className={styles.swatchSecondaryLight}></div>
                </div>
                <button
                  className={styles.button}
                  onClick={() => {
                    setUserTheme("default");
                  }}
                >
                  Activate
                </button>
              </div>
            </div>
            <div className={styles.themeOption}>
              <div className={styles.column}>
                <span className={styles.cardTitle}>Lavender Mist</span>
                <span className={styles.small}>Classic theme</span>
                <div className={`${styles.swatches} theme-ocean`}>
                  <div className={styles.swatchPrimary}></div>
                  <div className={styles.swatchSecondary}></div>
                  <div className={styles.swatchPrimaryLight}></div>
                  <div className={styles.swatchSecondaryLight}></div>
                </div>
                <button
                  className={styles.button}
                  onClick={() => {
                    setUserTheme("ocean");
                  }}
                >
                  Activate
                </button>
              </div>
            </div>
            <div className={styles.themeOption}>
              <div className={styles.column}>
                <span className={styles.cardTitle}>Dark</span>
                <span className={styles.small}>Dark mode theme</span>
                <div className={`${styles.swatches} theme-dark`}>
                  <div className={styles.swatchPrimary}></div>
                  <div className={styles.swatchSecondary}></div>
                  <div className={styles.swatchPrimaryLight}></div>
                  <div className={styles.swatchSecondaryLight}></div>
                </div>
                <button
                  className={styles.button}
                  onClick={() => {
                    setUserTheme("dark");
                  }}
                >
                  Activate
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div className={styles.grid}>
          <div>
            {/*Col 1*/}
            <div className={styles.card}>
              <div className={styles.cardBody}>
                <span className={styles.cardTitle}>Change Password</span>
                <span className={styles.small}>
                  Change the password you use to login
                </span>
                <button
                  className={styles.button}
                  onClick={() => {
                    setChangePasswordOpen(true);
                    setPasswords({
                      old_password: "",
                      new_password: "",
                      confirm_password: "",
                    });
                  }}
                >
                  Change password
                </button>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardBody}>
                <span className={styles.cardTitle}>Report Bug/Feedback</span>
                <span className={styles.small}>
                  Noticed a bug or have something to share with us? Let us know
                  here.
                </span>
                <button
                  className={styles.button}
                  onClick={() => {
                    openFeedback();
                  }}
                >
                  Report Bug/Feedback
                </button>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardBody}>
                <span className={styles.cardTitle}>Difficulty</span>
                <div className={styles.toggleRow}>
                  <span className={styles.toggleLabel}>Less Difficult</span>
                  <ToggleButton
                    text={undefined}
                    checked={difficultyLevel == "easy"}
                    setChecked={() => {
                      setDifficultyLevel("easy");
                    }}
                  />
                </div>
                <span className={styles.small}>
                  This selection will show fewer answer options than typical on
                  the real test.
                </span>
                <div className={styles.toggleRow}>
                  <span className={styles.toggleLabel}>Difficult </span>
                  <ToggleButton
                    text={undefined}
                    checked={difficultyLevel == "medium"}
                    setChecked={() => {
                      setDifficultyLevel("medium");
                    }}
                  />
                </div>
                <span className={styles.small}>
                  This selection will show roughly the same number of answer
                  options as on the real test.
                </span>
                <div className={styles.toggleRow}>
                  <span className={styles.toggleLabel}>Most Difficult</span>
                  <ToggleButton
                    text={undefined}
                    checked={difficultyLevel == "hard"}
                    setChecked={() => {
                      setDifficultyLevel("hard");
                    }}
                  />
                </div>
                <span className={styles.small}>
                  This selection will show many more answer options than what's
                  typical on the real test.
                </span>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardBody}>
                <span className={styles.cardTitle}>Warnings</span>
                <div className={styles.toggleRow}>
                  <span className={styles.toggleLabel}>Enabled</span>
                  <ToggleButton
                    text={undefined}
                    checked={!userData.hide_warning}
                    setChecked={() => {
                      updateWarnings({ warnings: !userData.hide_warning });
                      dispatch(
                        setUserDataWarnings({
                          hide_warning: !userData.hide_warning,
                        })
                      );
                    }}
                  />
                </div>
                <span className={styles.small}>
                  Warnings appear when you click on an action that requires a
                  confirmation
                </span>
              </div>
            </div>
          </div>
          {/*Col 2*/}
          <div>
            <div className={styles.card}>
              <div className={styles.cardBody}>
                <span className={styles.cardTitle}>Resubscribe</span>
                <span className={styles.small}>
                  You have{" "}
                  <span className={styles.bold}>
                    {daysUntil(new Date(userData.license_expiration))} days
                  </span>{" "}
                  remaining in your subscription ending on{" "}
                  <span className={styles.bold}>
                    {" "}
                    {formatDateTime(
                      timeZone.value,
                      new Date(userData.license_expiration)
                    )}{" "}
                  </span>
                  if you need more time to study you can resubsribe by following
                  these instructions
                </span>
                <div className={styles.steps}>
                  <div className={styles.stepFirst}>
                    <span className={styles.stepNumber}>1</span>
                    <span>
                      Click "Resubscribe" or navigate to "OsceDental Purchase
                      Link"
                    </span>
                  </div>
                  <div className={styles.toggleRow}>
                    <span className={styles.stepNumber}>2</span>
                    <span>
                      Select the amount of time that you would like to extend
                      your subscription
                    </span>
                  </div>
                  <div className={styles.toggleRow}>
                    <span className={styles.stepNumber}>3</span>
                    <span>
                      At checkout, use the same e-mail address that you used to
                      subscribe originally. The system will automatically locate
                      your account and extend your subscription.
                    </span>
                  </div>
                </div>
                <button
                  className={styles.button}
                  onClick={() => {
                    slugButton();
                  }}
                >
                  Resubscribe
                </button>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardBody}>
                <span className={styles.cardTitle}>Time Zone</span>
                <span className={styles.small}>
                  Setting your time zone will make device times like case
                  completion dates display in your local time instead of UTC
                </span>
                <div className={`select-wrapper ${styles.selectWrapper}`}>
                  <TimezoneSelect
                    value={selectedTimezone}
                    onChange={setUserTimeZone}
                    displayValue="UTC"
                    styles={{
                      control: (base) => ({
                        ...base,
                        color: "black",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "black",
                      }),
                      option: (base, { isFocused }) => ({
                        ...base,
                        color: isFocused ? "black" : "black",
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardBody}>
                <span className={styles.cardTitle}>Tooth Notation</span>
                <label className={styles.small} htmlFor="toothNotation">
                  Choose how tooth numbers are shown in questions and answers
                </label>
                <select
                  id="toothNotation"
                  className={styles.select}
                  value={toothNotation}
                  disabled={toothNotationSaving}
                  onChange={(e) => setUserToothNotation(e.target.value)}
                >
                  {TOOTH_NOTATIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardBody}>
                <span className={styles.cardTitle}>Print Receipt</span>
                <span className={styles.small}>
                  Need a copy of your account receipt? You can download a copy
                  or print it here
                </span>
                <div className={`select-wrapper ${styles.selectWrapper}`}>
                  <button
                    className={styles.buttonFull}
                    onClick={() => {
                      setReceiptOpen(true);
                    }}
                  >
                    Print Receipt
                  </button>
                </div>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardBody}>
                <span className={styles.cardTitle}>Resend receipt</span>
                <span className={styles.small}>
                  Need a copy of your account receipt email? You can have it
                  resent here
                </span>
                <div className={`select-wrapper ${styles.selectWrapper}`}>
                  <button
                    className={styles.buttonFull}
                    onClick={async () => {
                      try {
                        // Get the URL from the environment variable
                        const url =
                          import.meta.env.VITE_SLUG_URL + "/getslug.webapi";
                        console.log("About to send request to ", url);
                        if (!url) {
                          console.error(
                            "Environment variable VITE_SLUG_URL is not defined"
                          );
                          alert("Server URL is not configured properly");
                          return;
                        }

                        // Prepare the request body
                        const requestBody = {
                          email: userData.customer_email,
                          password: userData.password,
                          productName: "Oscedental",
                        };

                        // Make the POST request
                        const response = await fetch(url, {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify(requestBody),
                        });

                        // Handle the response
                        if (response.ok) {
                          const data = await response.json();
                          console.log("Success:", data);
                          if (data && data.container && data.container.Slug) {
                            const extractedSlug = data.container.Slug;
                            let product = "";
                            if (DLOSCEMode == "DLOSCE") {
                              product = "dloscedental";
                            } else {
                              product = "Oscedental";
                            }
                            const response = await fetch(
                              `${
                                import.meta.env.VITE_SLUG_URL
                              }/resendreceipt.webapi`,
                              {
                                method: "POST",
                                headers: {
                                  "Content-Type": "application/json",
                                },
                                body: JSON.stringify({
                                  Email: userData.customer_email,
                                  ProductName: product,
                                  slugContainer: {
                                    Slug: extractedSlug,
                                    productName: product,
                                  },
                                }),
                              }
                            );

                            if (!response.ok) {
                              throw new Error(
                                "Invalid or expired subscription link"
                              );
                            }

                            console.log("Extracted Slug:", extractedSlug);
                          } else {
                            console.error("Slug not found in the response");
                          }
                        } else {
                          console.error("Error:", response.statusText);
                          alert(`Request failed: ${response.statusText}`);
                        }
                      } catch (error) {
                        console.error("Error:", error);
                        alert(`An error occurred: ${error.message}`);
                      }
                    }}
                  >
                    Print Receipt
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        isOpen={changePasswordOpen}
        onClose={() => {
          setPasswordError("");
          setChangePasswordOpen(false);
        }}
        title={"Change password"}
        submit={true}
        onSubmit={() => {
          console.log("Submitting");
          setUserPassword();
        }}
        closeHidden={undefined}
        fullWidth={undefined}
        pixelWidth={undefined}
        submitText={undefined}
        cancelText={undefined}
        xClose={undefined}
        submitDark={undefined}
        closeDark={undefined}
        buttonsSpaced={undefined}
      >
        <div className={styles.passwordForm}>
          <form action="#">
            <div>
              <label
                className={styles.label}
                htmlFor="oldpassword"
              >
                Old Password
              </label>
              <div className={styles.inputRow}>
                <input
                  id="oldpassword"
                  name="oldpassword"
                  autoComplete="on"
                  type={showPassword ? "text" : "password"}
                  className={styles.input}
                  value={passwords.old_password}
                  onChange={(e) => {
                    setPasswords((prevState) => ({
                      ...prevState,
                      old_password: e.target.value,
                    }));
                  }}
                />
                <div
                  className={styles.showPassword}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <Eye />
                </div>
              </div>
            </div>
            <div>
              <label
                className={styles.label}
                htmlFor="password"
              >
                New Password
              </label>
              <div className={styles.inputRow}>
                <input
                  id="password"
                  name="password"
                  autoComplete="on"
                  type={showPassword ? "text" : "password"}
                  className={styles.input}
                  value={passwords.new_password}
                  onChange={(e) => {
                    setPasswords((prevState) => ({
                      ...prevState,
                      new_password: e.target.value,
                    }));
                  }}
                />
              </div>
            </div>
            <div>
              <label
                className={styles.label}
                htmlFor="newpassword"
              >
                Confirm New Password
              </label>
              <div className={styles.inputRow}>
                <input
                  id="newpassword"
                  name="newpassword"
                  autoComplete="on"
                  type={showPassword ? "text" : "password"}
                  className={styles.input}
                  value={passwords.confirm_password}
                  onChange={(e) => {
                    setPasswords((prevState) => ({
                      ...prevState,
                      confirm_password: e.target.value,
                    }));
                  }}
                />
              </div>
            </div>
            {passwordError && (
              <span className={styles.error}>{passwordError}</span>
            )}
          </form>
        </div>
      </Modal>
      <Modal
        isOpen={feedback.open}
        onClose={closeFeedback}
        title={`Feedback`}
        submit={feedback.status === "idle"}
        onSubmit={submitFeedback}
        closeHidden={undefined}
        fullWidth={undefined}
        pixelWidth={undefined}
        submitText={undefined}
        cancelText={undefined}
        xClose={undefined}
        submitDark={undefined}
        closeDark={undefined}
        buttonsSpaced={undefined}
      >
        {feedback.status === "success" || feedback.status === "error" ? (
          <div className={styles.spacedText}>
            <span>
              {feedback.status === "success"
                ? "Thank you, your feedback has been sent!"
                : "Failed to send feedback, please try again later."}
            </span>
          </div>
        ) : feedback.status === "loading" ? (
          <div>
            <span>Sending feedback...</span>
          </div>
        ) : (
          <textarea
            className={styles.feedbackTextarea}
            placeholder="Tell us if you have any thoughts about the app..."
            onChange={(event) => {
              const value = event.target.value;
              setFeedback((prev) => ({ ...prev, message: value }));
            }}
            value={feedback.message}
            autoComplete="off"
          />
        )}
      </Modal>
      <Modal
        isOpen={receiptOpen}
        onClose={() => {
          setReceiptOpen(false);
        }}
        title={`Print Your Receipt`}
        submit={true}
        onSubmit={async () => {
          fetchReceipt();
        }}
        closeHidden={undefined}
        fullWidth={undefined}
        pixelWidth={undefined}
        submitText={undefined}
        cancelText={undefined}
        xClose={undefined}
        submitDark={undefined}
        closeDark={undefined}
        buttonsSpaced={undefined}
      >
        <div className={styles.receiptForm}>
          <span>
            If you need the last four digits of your card added to your receipt,
            please enter them here.
          </span>
          <div className={styles.receiptField}>
            <span>Last Four Digits of Card</span>
            <input
              id="card"
              name="card"
              type={"text"}
              className={styles.input}
              placeholder="Last 4"
              value={last4Dgitis}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value) && value.length <= 4) {
                  setLast4Digits(value);
                }
              }}
            />
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={htmlContent != ""}
        onClose={() => {
          setHtmlContent("");
          setReceiptOpen(false);
        }}
        title={"Receipt"}
        submit={true}
        onSubmit={() => {
          handlePrint();
        }}
        fullWidth={true}
        closeHidden={undefined}
        pixelWidth={undefined}
        cancelText={undefined}
        xClose={undefined}
        submitDark={undefined}
        closeDark={undefined}
        buttonsSpaced={undefined}
        submitText={"Print"}
      >
        <div className={styles.receiptScroll}>
          <div
            dangerouslySetInnerHTML={{ __html: htmlContent }}
            className={styles.receiptHtml}
          />
        </div>
      </Modal>
    </>
  );
}

export default AccountSettings;